import { useContext, useEffect, useState } from "react";

import {
  getUserIdFromRefreshToken,
  isLoggedIn,
  LogSentryException,
  PageContainer,
  SpinnerCenter,
} from "@shapr3d/frontend-lib";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import UnderMaintenance from "../components/UnderMaintenance/UnderMaintenance";
import { legacyURLs } from "../config";
import SignInContainer from "../containers/SignInContainer";
import { DBAvailableContext } from "../contexts/DBAvailableProvider";
import { useVerifyAuthHandoff } from "../hooks/useAuth";
import { useTracker } from "../hooks/useTracker";
import { SearchParam } from "../types";
import { PageSource } from "../types/tracking";
import { sanitizeRedirectUrl } from "../urlUtils";
import { saveLoggedInUser } from "../utils/authUtils";

// TODO we should optimize this to transform to a relative redirect instead
// but we need to rewire the navigation for that
const transformRedirectURL = (targetURL: URL) => {
  const isLegacyURL = legacyURLs.includes(targetURL.origin);
  if (isLegacyURL) {
    const url = new URL(window.location.origin);
    url.pathname = targetURL.pathname;
    url.search = targetURL.search;
    url.hash = targetURL.hash;
    return url;
  }

  return targetURL;
};

const AuthHandoffPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tracker = useTracker();
  const { isDBDown } = useContext(DBAvailableContext);

  const [isLoading, setIsLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const returnUrl = new URLSearchParams(location.search).get(SearchParam.RedirectUrl);

  const [token, setToken] = useState("");
  useEffect(() => {
    // Temporary account prod handoff --to--> beta app redirect until we phase out old beta apps
    if (
      searchParams.get(SearchParam.RedirectUrl) === "https://beta.collaborate.shapr3d.com" &&
      window.location.origin === "https://app.shapr3d.com"
    ) {
      const url = new URL("https://beta.app.shapr3d.com");
      url.pathname = window.location.pathname;
      url.search = window.location.search;
      url.hash = window.location.hash;
      window.location.replace(url);
    } else if (location.hash || token === "") {
      const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
      const newToken = hashParams.get("token") ?? "error"; // Empty and special 'error' token both means error
      setToken(newToken);
      setSearchParams(
        (searchParams) => {
          const sanitizedRedirectURL = sanitizeRedirectUrl(
            searchParams.get(SearchParam.RedirectUrl) ?? ""
          );
          const transformedRedirectURL = transformRedirectURL(sanitizedRedirectURL);
          transformedRedirectURL.searchParams.set(SearchParam.Source, PageSource.AuthHandoff);
          searchParams.set(SearchParam.RedirectUrl, transformedRedirectURL.toString());
          return searchParams;
        },
        { replace: true }
      );
    }
  }, [location, token, navigate, setSearchParams, searchParams]);

  const { mutateAsync: mutateVerifyAuthHandoff } = useVerifyAuthHandoff();

  useEffect(() => {
    if (token !== "") {
      if (token !== "error") {
        const verifyToken = async () => {
          try {
            await mutateVerifyAuthHandoff(token, {
              onSuccess: (data) => {
                const previouslyLoggedinUserId = getUserIdFromRefreshToken();

                if (
                  isLoggedIn() &&
                  previouslyLoggedinUserId &&
                  previouslyLoggedinUserId !== data.id
                ) {
                  tracker.reset();
                }
                saveLoggedInUser(data);

                const redirectURL = searchParams.get(SearchParam.RedirectUrl);
                window.location.replace(redirectURL ?? "/");
              },
            });
          } catch (err) {
            LogSentryException({ messagePrefix: "Auth handoff verify error", error: err });
            setIsLoading(false);
          }
        };
        verifyToken();
      } else {
        setIsLoading(false);
      }
    }
  }, [navigate, token, returnUrl, mutateVerifyAuthHandoff, tracker, searchParams]);

  if (isDBDown) {
    return <UnderMaintenance />;
  }

  if (isLoading) {
    return (
      <PageContainer>
        <SpinnerCenter />
      </PageContainer>
    );
  }

  return <SignInContainer isAuthHandoffFailed />;
};

export default AuthHandoffPage;
